import { supabase } from './supabase';
import type { 
  Receipt, 
  PaymentHistory, 
  FinancialSummary, 
  ReferralCommissionSummary,
  Invoice,
  Payment
} from './supabase';

export const accountsService = {
  async getPaymentHistory(filters?: {
    startDate?: string;
    endDate?: string;
    patientId?: string;
    paymentMethod?: string;
  }) {
    let query = supabase
      .from('payment_history')
      .select('*')
      .order('payment_date', { ascending: false });

    if (filters?.startDate) {
      query = query.gte('payment_date', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('payment_date', filters.endDate);
    }
    if (filters?.patientId) {
      query = query.eq('patient_id', filters.patientId);
    }
    if (filters?.paymentMethod) {
      query = query.eq('payment_method', filters.paymentMethod);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as PaymentHistory[];
  },

  async getFinancialSummary(filters?: {
    startDate?: string;
    endDate?: string;
    referenceType?: string;
  }) {
    let query = supabase
      .from('financial_summary')
      .select('*')
      .order('date', { ascending: false });

    if (filters?.startDate) {
      query = query.gte('date', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('date', filters.endDate);
    }
    if (filters?.referenceType) {
      query = query.eq('reference_type', filters.referenceType);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as FinancialSummary[];
  },

  async getReferralCommissionSummary() {
    const { data, error } = await supabase
      .from('referral_commission_summary')
      .select('*')
      .order('total_commission', { ascending: false });

    if (error) throw error;
    return data as ReferralCommissionSummary[];
  },

  async getRevenueAnalytics(period: 'day' | 'week' | 'month' | 'year' = 'month') {
    const { data, error } = await supabase.rpc('get_revenue_analytics', {
      period_type: period
    });

    if (error) {
      const summary = await this.getFinancialSummary();
      return this.calculateRevenueAnalytics(summary, period);
    }
    return data;
  },

  calculateRevenueAnalytics(summary: FinancialSummary[], period: string) {
    const grouped: { [key: string]: FinancialSummary } = {};
    
    summary.forEach(item => {
      const date = new Date(item.date);
      let key: string;
      
      switch (period) {
        case 'day':
          key = item.date;
          break;
        case 'week':
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          key = weekStart.toISOString().split('T')[0];
          break;
        case 'month':
          key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          break;
        case 'year':
          key = String(date.getFullYear());
          break;
        default:
          key = item.date;
      }

      if (!grouped[key]) {
        grouped[key] = { ...item, date: key };
      } else {
        grouped[key].total_invoices += item.total_invoices;
        grouped[key].total_subtotal += item.total_subtotal;
        grouped[key].total_discounts += item.total_discounts;
        grouped[key].total_tax += item.total_tax;
        grouped[key].total_revenue += item.total_revenue;
        grouped[key].total_collected += item.total_collected;
        grouped[key].total_outstanding += item.total_outstanding;
        grouped[key].total_refunds += item.total_refunds;
        grouped[key].paid_invoices += item.paid_invoices;
        grouped[key].partial_invoices += item.partial_invoices;
        grouped[key].overdue_invoices += item.overdue_invoices;
      }
    });

    return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date));
  },

  async getPatientFinancialSummary(patientId: string) {
    const { data: invoices, error: invoicesError } = await supabase
      .from('invoices')
      .select('*')
      .eq('patient_id', patientId);

    if (invoicesError) throw invoicesError;

    const { data: payments, error: paymentsError } = await supabase
      .from('payments')
      .select('*, invoice:invoices!inner(patient_id)')
      .eq('invoice.patient_id', patientId);

    if (paymentsError) throw paymentsError;

    const totalInvoiced = invoices?.reduce((sum, inv) => sum + Number(inv.total), 0) || 0;
    const totalPaid = payments?.reduce((sum, pay) => sum + Number(pay.amount), 0) || 0;
    const totalOutstanding = invoices?.reduce((sum, inv) => sum + Number(inv.balance_due), 0) || 0;
    const totalDiscounts = invoices?.reduce((sum, inv) => sum + Number(inv.discount_amount || 0), 0) || 0;
    const totalRefunds = invoices?.reduce((sum, inv) => sum + Number(inv.refund_amount || 0), 0) || 0;

    return {
      totalInvoiced,
      totalPaid,
      totalOutstanding,
      totalDiscounts,
      totalRefunds,
      invoiceCount: invoices?.length || 0,
      paymentCount: payments?.length || 0,
      lastPaymentDate: payments?.[0]?.payment_date,
    };
  },

  async getOutstandingDues(filters?: {
    minAmount?: number;
    maxDays?: number;
  }) {
    let query = supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*)
      `)
      .gt('balance_due', 0)
      .order('due_date', { ascending: true });

    if (filters?.minAmount) {
      query = query.gte('balance_due', filters.minAmount);
    }

    const { data, error } = await query;
    if (error) throw error;

    let result = data as Invoice[];

    if (filters?.maxDays) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - filters.maxDays);
      result = result.filter(inv => 
        inv.due_date && new Date(inv.due_date) >= cutoffDate
      );
    }

    return result;
  },

  async getDiscountReport(filters?: {
    startDate?: string;
    endDate?: string;
    minAmount?: number;
  }) {
    let query = supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*)
      `)
      .gt('discount_amount', 0)
      .order('issue_date', { ascending: false });

    if (filters?.startDate) {
      query = query.gte('issue_date', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('issue_date', filters.endDate);
    }
    if (filters?.minAmount) {
      query = query.gte('discount_amount', filters.minAmount);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as Invoice[];
  },

  async getRefundReport(filters?: {
    startDate?: string;
    endDate?: string;
  }) {
    let query = supabase
      .from('invoices')
      .select(`
        *,
        patient:patients(*)
      `)
      .gt('refund_amount', 0)
      .order('refund_date', { ascending: false });

    if (filters?.startDate) {
      query = query.gte('refund_date', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('refund_date', filters.endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as Invoice[];
  },

  async processRefund(invoiceId: string, refundData: {
    refund_amount: number;
    refund_reason: string;
  }) {
    const { data, error } = await supabase
      .from('invoices')
      .update({
        refund_amount: refundData.refund_amount,
        refund_reason: refundData.refund_reason,
        refund_date: new Date().toISOString().split('T')[0],
        status: 'refunded',
        updated_at: new Date().toISOString()
      })
      .eq('id', invoiceId)
      .select()
      .single();

    if (error) throw error;
    return data as Invoice;
  },

  async applyDiscount(invoiceId: string, discountAmount: number) {
    const { data: invoice, error: fetchError } = await supabase
      .from('invoices')
      .select('*')
      .eq('id', invoiceId)
      .single();

    if (fetchError) throw fetchError;

    const newTotal = Number(invoice.subtotal) + Number(invoice.tax_amount) - discountAmount;
    const newBalanceDue = newTotal - Number(invoice.amount_paid);

    const { data, error } = await supabase
      .from('invoices')
      .update({
        discount_amount: discountAmount,
        total: newTotal,
        balance_due: newBalanceDue,
        updated_at: new Date().toISOString()
      })
      .eq('id', invoiceId)
      .select()
      .single();

    if (error) throw error;
    return data as Invoice;
  },
};

export const receiptService = {
  async getAll(filters?: {
    patientId?: string;
    receiptType?: string;
    startDate?: string;
    endDate?: string;
  }) {
    let query = supabase
      .from('receipts')
      .select(`
        *,
        invoice:invoices(*),
        payment:payments(*),
        patient:patients(*)
      `)
      .order('generated_at', { ascending: false });

    if (filters?.patientId) {
      query = query.eq('patient_id', filters.patientId);
    }
    if (filters?.receiptType) {
      query = query.eq('receipt_type', filters.receiptType);
    }
    if (filters?.startDate) {
      query = query.gte('generated_at', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('generated_at', filters.endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as Receipt[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('receipts')
      .select(`
        *,
        invoice:invoices(*),
        payment:payments(*),
        patient:patients(*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as Receipt;
  },

  async generateReceiptNumber(): Promise<string> {
    const { data, error } = await supabase.rpc('generate_receipt_number');
    
    if (error) {
      return `RCP-${Date.now()}`;
    }
    
    return data as string;
  },

  async create(receiptData: {
    receipt_type: 'standard' | 'detailed' | 'summary';
    invoice_id?: string;
    payment_id?: string;
    patient_id: string;
    amount: number;
    payment_method?: string;
    generated_by?: string;
    metadata?: any;
  }) {
    const receiptNumber = await this.generateReceiptNumber();

    const { data, error } = await supabase
      .from('receipts')
      .insert([{
        ...receiptData,
        receipt_number: receiptNumber,
        generated_at: new Date().toISOString()
      }])
      .select(`
        *,
        invoice:invoices(*),
        payment:payments(*),
        patient:patients(*)
      `)
      .single();

    if (error) throw error;
    return data as Receipt;
  },

  async markAsPrinted(id: string) {
    const { data, error } = await supabase
      .from('receipts')
      .update({
        printed_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Receipt;
  },

  async updatePdfUrl(id: string, pdfUrl: string) {
    const { data, error } = await supabase
      .from('receipts')
      .update({
        pdf_url: pdfUrl
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Receipt;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('receipts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },
};
